import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { Config851DataService } from './config851-data.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-config851-data',
    templateUrl: './config851-data.component.html',
    styleUrls: ['./config851-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Config851DataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  minimise: boolean = false;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any;
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;
  rowData: any = {};

  // Standardized component properties
  currentTabName: string = 'Form Entry';
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;
  modalTitle: string = '851 Config Data Setup';
  showFormLoader: boolean = true;

  // Data from inputs
  globalParams: any = {};
  userSettingsData: any = {};

  // Entity table
  entityList: Array<{ key: string; value: any }> = [];

  // Form data
  configEntity: any = {};
  configEntity_bckup: any = {};
  isEdit: boolean = false;

  // Dropdown data
  allscenarioTypeCode: any[] = [];
  allscenarioDesc: any[] = [];

  // Date picker states
  dateOptions: any = {};

  // API completion flags
  scenarioTypeCodeApiCompleted = false;
  scenarioDescApiCompleted = false;

  constructor(
    private service: Config851DataService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initializeService();
      this.loadInitialData();
    }));
    this.initializeService();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.globalParams = header.globalParams || {};
    this.userSettingsData = this.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  /**
   * Initialize service with base URLs and parameters
   */
  private initializeService(): void {
    if (this.baseURLs && this.globalParams && this.filterParams) {
      console.log('Initializing service with:', {
        baseURLs: this.baseURLs,
        globalParams: this.globalParams,
        filterParams: this.filterParams
      });

      // Validate critical configuration
      if (!this.baseURLs?.api) {
        console.error('Service initialization warning: API base URL is missing');
      }
      
      if (!this.filterParams?.tax_year) {
        console.error('Service initialization warning: Tax year is missing from filter params');
      }

      if (this.globalParams == null) {
        console.error('Service initialization warning: Global params is missing');
      }

      console.log('Service initialized successfully');
    }
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    if (!this.baseURLs?.api || !this.globalParams || !this.filterParams) {
      return;
    }

    this.showFormLoader = true;
    this.setupEntityData();
    this.initializeComponent();
  }

  /**
   * Check if all API calls are completed
   */
  private checkLoadingCompletion(): void {
    if (this.scenarioTypeCodeApiCompleted) {
      this.showFormLoader = false;
    }
  }

  private setupEntityData(): void {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.entityList = [
      { key: 'Tax Year', value: this.rowData?.TAX_YEAR || this.filterParams?.tax_year || '' },
      { key: 'LEID', value: this.rowData?.LEID || '' },
      { key: 'LE Name', value: this.rowData?.LE_NAME || '' }
    ];
  }

  private initializeComponent(): void {
    // Determine if this is edit mode based on rowData
    this.isEdit = this.rowData && Object.keys(this.rowData).length > 0;
    
    if (this.isEdit) {
      this.configEntity_bckup = { ...this.rowData };
      this.configEntity_bckup.BATCH_START_DT = this.rowData.BATCH_START_DT ? new Date(this.rowData.BATCH_START_DT) : new Date();
      this.configEntity_bckup.BATCH_END_DT = this.configEntity_bckup.SCHEDULE_IND === 'I' ? 
        (this.rowData.BATCH_END_DT ? new Date(this.rowData.BATCH_END_DT) : '') : '';
    } else {
      this.configEntity_bckup = {
        TAX_YEAR: this.filterParams?.tax_year || '',
        SCENARIO: '',
        JCD_KEY: this.rowData?.JCD_KEY || this.globalParams?.jcd_key || '',
        SCHEDULE_IND: 'A',
        BATCH_START_DT: null,  // No initial date for create mode
        BATCH_END_DT: null,    // No initial date for create mode
        SCENARIO_CODE: '',
      };
    }
    
    this.configEntity = { ...this.configEntity_bckup };
    this.loadScenarioTypeCode();
    // this.loadScenarioDesc();
  }

  loadScenarioTypeCode(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.globalParams || !this.filterParams) {
      return ;
    }
    this.service.loadScenarioTypeCode(
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '',
      this.globalParams,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
                if (response?.jsonObject) {
          this.allscenarioTypeCode = response.jsonObject;
          this.scenarioTypeCodeApiCompleted = true;
          this.checkLoadingCompletion();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading scenario type codes:', error);
        this.scenarioTypeCodeApiCompleted = true;
        this.checkLoadingCompletion();
        this.cdr.markForCheck();
      }
    );
  }

  loadScenarioDesc(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.globalParams || !this.filterParams || !this.configEntity.SCENARIO_CODE) {
      return ;
    }
    
    this.service.loadScenarioDesc(
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '',
      this.configEntity.SCENARIO_CODE,
      this.globalParams,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.jsonObject && response.jsonObject.length > 0) {
          this.allscenarioDesc = response.jsonObject;
          this.configEntity.SCENARIO_DESC = response.jsonObject[0].SCENARIO_DESC;
          this.configEntity.SCENARIO = response.jsonObject[0].SCENARIO_VALUE;
          this.scenarioDescApiCompleted = true;
          this.checkLoadingCompletion();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading scenario desc:', error);
        this.scenarioDescApiCompleted = true;
        this.checkLoadingCompletion();
        this.cdr.markForCheck();
      }
    );
  }

  scheduleChanged(): void {
    if (this.configEntity.SCHEDULE_IND === 'A') {
      this.configEntity.BATCH_END_DT = null;
    } else {
      // Only set date if we're in edit mode, otherwise leave empty for create mode
      if (this.isEdit) {
        this.configEntity.BATCH_END_DT = new Date();
      } else {
        this.configEntity.BATCH_END_DT = null;
      }
    }
  }

  onDateChange(type: string, event?: any): void {
    if (type === 'start') {
      if (event && event.target && event.target.value) {
        this.configEntity.BATCH_START_DT = new Date(event.target.value);
      }
    } else if (type === 'end') {
      if (event && event.target && event.target.value) {
        this.configEntity.BATCH_END_DT = new Date(event.target.value);
      }
    }
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  // Helper methods for date inputs
  get batchStartDateFormatted(): string {
    if (!this.configEntity.BATCH_START_DT) {
      return '';
    }
    const date = new Date(this.configEntity.BATCH_START_DT);
    if (isNaN(date.getTime())) {
      return '';
    }
    return this.formatDateForInput(date);
  }

  set batchStartDateFormatted(value: string) {
    if (value) {
      this.configEntity.BATCH_START_DT = new Date(value);
    } else {
      this.configEntity.BATCH_START_DT = null;
    }
  }

  get batchEndDateFormatted(): string {
    if (!this.configEntity.BATCH_END_DT) return '';
    const date = new Date(this.configEntity.BATCH_END_DT);
    if (isNaN(date.getTime())) return '';
    return this.formatDateForInput(date);
  }

  set batchEndDateFormatted(value: string) {
    if (value) {
      this.configEntity.BATCH_END_DT = new Date(value);
    } else {
      this.configEntity.BATCH_END_DT = null;
    }
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getFormattedDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    console.log('Formatted date:', `${month}/${day}/${year}`);
    return `${month}/${day}/${year}`;
  }

  isValid(): boolean {
    // Clear previous validation errors
    this.configEntity.TAX_YEAR_NOT_VALID = false;
    this.configEntity.SCENARIO_NOT_VALID = false;
    this.configEntity.SCENARIO_CODE_NOT_VALID = false;
    this.configEntity.JCD_KEY_NOT_VALID = false;
    this.configEntity.SCHEDULE_IND_NOT_VALID = false;
    this.configEntity.BATCH_START_DT_NOT_VALID = false;

    if (!this.configEntity.TAX_YEAR) {
      this.configEntity.TAX_YEAR_NOT_VALID = true;
      return false;
    }
    if (!this.configEntity.SCENARIO) {
      this.configEntity.SCENARIO_NOT_VALID = true;
      return false;
    }
    if (!this.configEntity.SCENARIO_CODE) {
      this.configEntity.SCENARIO_CODE_NOT_VALID = true;
      return false;
    }
    if (!this.configEntity.JCD_KEY) {
      this.configEntity.JCD_KEY_NOT_VALID = true;
      return false;
    }
    if (!this.configEntity.SCHEDULE_IND) {
      this.configEntity.SCHEDULE_IND_NOT_VALID = true;
      return false;
    }
    if (!this.configEntity.BATCH_START_DT) {
      this.configEntity.BATCH_START_DT_NOT_VALID = true;
      return false;
    }
    return true;
  }

  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const saveData = this.buildSaveData();
    const settingsObj = this.buildSettings();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    this.service.saveConfigDataForm(
      baseUrl,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData,
      settingsObj
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response?.callSuccess === "1" && response?.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Config Details Updated successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  delete(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const saveData = this.buildSaveData();
    saveData.ACTION = 'DELETE';
    const settingsObj = this.buildSettings();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    this.service.saveConfigDataForm(
      baseUrl,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData,
      settingsObj
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response?.callSuccess === "1" && response?.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Error occurred while deleting', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  reset(): void {
    this.configEntity = { ...this.configEntity_bckup };
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-config851-data-ce')) {
      document.querySelector('gtw-config851-data-ce')?.dispatchEvent(updateEvent);
    }
  }

  private buildSettings(): any {
    return {
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY,
      combination_key: this.filterParams?.combination_key || this.rowData?.COMBINATION_KEY,
      trans_type_key: this.filterParams?.trans_type_key || this.rowData?.TRANS_TYPE_KEY,
      scenario_code: this.filterParams?.scenario_code || this.rowData?.SCENARIO_CODE || this.globalParams?.scenario_code,
      is_issue_key_enabled: 'N'
    };
  }

  private buildSaveData(): any {
    const saveData = { ...this.configEntity };
    
    // Set action based on edit mode
    if (this.isEdit) {
      saveData.ACTION = 'UPDATE';
    } else {
      saveData.ACTION = 'INSERT';
    }

    // Add required parameters from filterParams and globalParams
    saveData.TAX_YEAR = this.filterParams?.tax_year || this.globalParams?.tax_year;
    saveData.SCENARIO = this.filterParams?.scenario || this.globalParams?.scenario;
    // saveData.SCENARIO_CODE = this.filterParams?.scenario_code || this.globalParams?.scenario_code;
    saveData.JCD_KEY = this.filterParams?.jcd_key || this.globalParams?.jcd_key;
    saveData.JCD_TA_KEY = this.filterParams?.jcd_ta_key || this.globalParams?.jcd_ta_key;
    saveData.SCREEN_KEY = this.filterParams?.screen_key || this.globalParams?.screen_key;
    saveData.SCREEN_TYPE = this.filterParams?.screen_type || this.globalParams?.screen_type || 'grid';
    saveData.PROCESS_NAME = this.filterParams?.process_name || this.globalParams?.process_name;
    saveData.FILING_KEY = this.filterParams?.filing_key || this.globalParams?.filing_key || this.rowData?.CONSOL_GROUP_KEY;

    // Format dates
    if (saveData.BATCH_START_DT instanceof Date) {
      saveData.BATCH_START_DT = this.getFormattedDate(saveData.BATCH_START_DT);
    } else if (!saveData.BATCH_START_DT) {
      saveData.BATCH_START_DT = '';
    }
    
    if (saveData.BATCH_END_DT && saveData.BATCH_END_DT instanceof Date) {
      saveData.BATCH_END_DT = this.getFormattedDate(saveData.BATCH_END_DT);
    } else if (!saveData.BATCH_END_DT) {
      saveData.BATCH_END_DT = '';
    }

    return saveData;
  }

}
